import React, { forwardRef, useImperativeHandle, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert, AlertTitle } from "@/components/ui/alert";
import { ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { LoaderCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

const applicationSettingsSchema = z.object({
    base_url: z.string().url("Please enter a valid URL").min(1, "Base URL is required"),
});

type ApplicationSettingsData = z.infer<typeof applicationSettingsSchema>;

type Props = {
    data?: ApplicationSettingsData;
    initialData?: any;
    onDataChange?: (data: any) => void;
    readOnly?: boolean;
    error?: string;
    submitButton?: React.ReactNode;
    onSubmit?: (data: any) => void;
    hideSubmitButton?: boolean;
    loading?: boolean;
};

const ApplicationSettingsForm = forwardRef<any, Props>(
    (
        {
            data,
            initialData = {},
            onDataChange,
            readOnly = false,
            error,
            submitButton,
            onSubmit,
            hideSubmitButton = false,
            loading = false,
        },
        ref
    ) => {
        const { t } = useTranslation();
        const form = useForm<ApplicationSettingsData>({
            resolver: zodResolver(applicationSettingsSchema),
            defaultValues: {
                base_url: initialData?.base_url || data?.base_url || "",
            },
        });

        // Watch for changes and notify parent component
        useEffect(() => {
            const subscription = form.watch((value) => {
                if (onDataChange) {
                    onDataChange(value);
                }
            });

            return () => subscription.unsubscribe();
        }, [form, onDataChange]);

        // Expose methods to parent component
        useImperativeHandle(ref, () => ({
            validateForm: async () => {
                const result = await form.trigger();
                if (!result) {
                    // Get the first error message
                    const errors = form.formState.errors;
                    const firstError = Object.values(errors)[0];
                    if (firstError?.message) {
                        console.error("Application Settings validation error:", firstError.message);
                    }
                }
                return result;
            },
            getFormData: () => {
                return form.getValues();
            },
            submitForm: () => {
                if (onSubmit) {
                    form.handleSubmit(onSubmit)();
                }
            },
        }));

        return (
            <Form {...form}>
                <form onSubmit={form.handleSubmit((data) => onSubmit && onSubmit(data))}>
                    <div>
                        {error && (
                            <Alert variant="destructive" className="mb-2 dark:bg-red-800 dark:text-white">
                                <ExclamationTriangleIcon className="h-4 w-4" />
                                <AlertTitle>{error}</AlertTitle>
                            </Alert>
                        )}

                        <div className="space-y-4">
                            <FormField
                                control={form.control}
                                name="base_url"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t("settings.applicationSettings.baseUrl")}</FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder={t("settings.applicationSettings.baseUrlPlaceholder")}
                                                {...field}
                                                readOnly={readOnly}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <div className="rounded-lg border p-4">
                                <p className="text-sm">
                                    {t("settings.applicationSettings.baseUrlDescription")}
                                </p>
                            </div>
                        </div>

                        {!hideSubmitButton && (
                            <div className="mt-6">
                                {submitButton || (
                                    <Button type="submit" disabled={loading}>
                                        {loading ? (
                                            <>
                                                <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                                                {t("common.loading")}
                                            </>
                                        ) : (
                                            t("common.save")
                                        )}
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                </form>
            </Form>
        );
    }
);

ApplicationSettingsForm.displayName = "ApplicationSettingsForm";

export default ApplicationSettingsForm;
